import React, {FC, useCallback, useEffect, useMemo, useState} from "react";
import {Alert, App, Button, Card, Carousel, Col, Empty, Form, Input, Modal, Row, Space, Tooltip, Typography} from "antd";
import {CalendarOutlined, CopyOutlined, GlobalOutlined, HistoryOutlined, LoginOutlined, PlusOutlined, RightOutlined, StarFilled, TrophyOutlined} from "@ant-design/icons";
import AxiosServices from "../Services/axiosServices";
import {ICmpEdt} from "../Interfaces/ICmpEdt";
import {IMatch, IsMatchInProgress} from "../Interfaces/IMatch";
import {useAuth} from "../config/AuthContext";
import {displayMatchDate, displayMatchTime, matchAsUtcDate} from "../Services/datetime";

const ACCENT = '#203923';
const {Title, Text} = Typography;

const UPCOMING_MATCH_PREVIEW = 4;

const CountdownBlock: FC<{label: string; value: number}> = ({label, value}) => (
    <div
        style={{
            background: 'rgba(255,255,255,0.14)',
            borderRadius: 10,
            padding: '10px 16px',
            minWidth: 72,
            textAlign: 'center',
        }}
    >
        <div
            style={{
                fontSize: 36,
                fontWeight: 700,
                color: '#fff',
                fontVariantNumeric: 'tabular-nums',
                lineHeight: 1,
            }}
        >
            {String(value).padStart(2, '0')}
        </div>
        <div
            style={{
                fontSize: 10,
                color: 'rgba(255,255,255,0.65)',
                textTransform: 'uppercase',
                letterSpacing: 1,
                marginTop: 6,
            }}
        >
            {label}
        </div>
    </div>
);

type CmpEdtRow = ICmpEdt & {Idt: number};
type CmpStatus = 'enCours' | 'aVenir' | 'passee';
type MyReg = {idt: number; Lib: string; Token: string; CmpEdt?: {idt: number}};

const todayMidnight = (): number => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d.getTime();
};

const dateTs = (raw: Date | string | undefined): number | null => {
    if (!raw) return null;
    const d = new Date(raw as any);
    if (Number.isNaN(d.getTime())) return null;
    d.setHours(0, 0, 0, 0);
    return d.getTime();
};

const classify = (cmp: CmpEdtRow): CmpStatus => {
    const today = todayMidnight();
    const deb = dateTs(cmp.DteDeb);
    const fin = dateTs(cmp.DteFin);
    if (deb !== null && deb > today) return 'aVenir';
    if (fin !== null && fin < today) return 'passee';
    return 'enCours';
};

const Home: FC = () => {
    const {isLoggedIn} = useAuth();
    const {message} = App.useApp();
    const [competitions, setCompetitions] = useState<CmpEdtRow[]>([]);
    const [selected, setSelected] = useState<CmpEdtRow | null>(null);
    const [creating, setCreating] = useState(false);
    const [createdToken, setCreatedToken] = useState<string | null>(null);
    const [pastModalOpen, setPastModalOpen] = useState(false);
    // Raw matches of the featured competition, fetched once per featuredCmp.
    // topMatches and carouselMatches are derived from this list against the
    // live `now` so transitions (upcoming → in-progress → past) bubble up
    // without needing a refetch.
    const [bannerMatches, setBannerMatches] = useState<IMatch[]>([]);
    const [myRegs, setMyRegs] = useState<MyReg[]>([]);
    const [now, setNow] = useState(Date.now());
    const [joinModalOpen, setJoinModalOpen] = useState(false);
    const [joining, setJoining] = useState(false);
    const [form] = Form.useForm();
    const [joinForm] = Form.useForm();

    useEffect(() => {
        const id = setInterval(() => setNow(Date.now()), 1000);
        return () => clearInterval(id);
    }, []);

    useEffect(() => {
        AxiosServices.getCpmEdt((data: CmpEdtRow[]) => setCompetitions(data ?? []))
            .catch((err: any) => {
                console.error('getCpmEdt failed:', err);
                message.error("Impossible de charger les compétitions.");
            });
    }, [message]);

    const refreshMyRegs = useCallback(() => {
        if (!isLoggedIn) {
            setMyRegs([]);
            return;
        }
        AxiosServices.listMyRegs()
            .then(data => setMyRegs(data ?? []))
            .catch((err: any) => console.error('listMyRegs failed:', err));
    }, [isLoggedIn]);

    useEffect(() => {
        refreshMyRegs();
    }, [refreshMyRegs]);

    const regsByCmpEdt = useMemo(() => {
        const map = new Map<number, MyReg[]>();
        myRegs.forEach(r => {
            const k = r.CmpEdt?.idt;
            if (!k) return;
            const list = map.get(k) ?? [];
            list.push(r);
            map.set(k, list);
        });
        return map;
    }, [myRegs]);

    const myCompetitions = useMemo<CmpEdtRow[]>(() => {
        return competitions
            .filter(c => regsByCmpEdt.has(c.Idt))
            .sort((a, b) => b.Idt - a.Idt);
    }, [competitions, regsByCmpEdt]);

    const {enCours, aVenir, passees} = useMemo(() => {
        const sortByIdtDesc = (a: CmpEdtRow, b: CmpEdtRow) => b.Idt - a.Idt;
        const buckets: Record<CmpStatus, CmpEdtRow[]> = {enCours: [], aVenir: [], passee: []};
        competitions.forEach(c => buckets[classify(c)].push(c));
        return {
            enCours: buckets.enCours.sort(sortByIdtDesc),
            aVenir: buckets.aVenir.sort(sortByIdtDesc),
            passees: buckets.passee.sort(sortByIdtDesc),
        };
    }, [competitions]);

    // Banner picker: in-progress OR strictly upcoming, oldest start first.
    // Drives the countdown card; first in-progress entry wins. Derived
    // from the raw bannerMatches list against the live `now` so a match
    // transition (upcoming → in-progress → past) propagates within the
    // second without a refetch.
    const topMatches = useMemo<IMatch[]>(() => {
        return bannerMatches
            .map(m => ({m, start: matchAsUtcDate(m.Dte, m.Hre)}))
            .filter(({m, start}) => start !== null && (start.getTime() > now || IsMatchInProgress(m)))
            .sort((a, b) => (a.start as Date).getTime() - (b.start as Date).getTime())
            .map(({m}) => m)
            .slice(0, UPCOMING_MATCH_PREVIEW);
    }, [bannerMatches, now]);

    // Carousel "Prochains matchs": strictly upcoming, never in-progress
    // (the banner already shows that one) and never past, since the
    // filter is recomputed on every `now` tick.
    const carouselMatches = useMemo<IMatch[]>(() => {
        return bannerMatches
            .map(m => ({m, start: matchAsUtcDate(m.Dte, m.Hre)}))
            .filter(({start}) => start !== null && start.getTime() > now)
            .sort((a, b) => (a.start as Date).getTime() - (b.start as Date).getTime())
            .map(({m}) => m)
            .slice(0, UPCOMING_MATCH_PREVIEW);
    }, [bannerMatches, now]);

    // Drives the banner card. Prefers a match currently in progress
    // (kickoff → kickoff + 120 min, see IsMatchInProgress) over the next
    // upcoming one; topMatches now keeps both flavours so the picker just
    // walks the list. `now` ticks every second so the elapsed/remaining
    // figures live-update.
    const countdown = useMemo(() => {
        for (const m of topMatches) {
            if (IsMatchInProgress(m)) {
                const start = matchAsUtcDate(m.Dte, m.Hre);
                if (!start) continue;
                const elapsedMs = now - start.getTime();
                const elapsedMinutes = Math.max(0, Math.floor(elapsedMs / 60_000));
                const elapsedSeconds = Math.max(0, Math.floor((elapsedMs % 60_000) / 1000));
                return {
                    kind: 'inProgress' as const,
                    match: m,
                    start,
                    elapsedMinutes,
                    elapsedSeconds,
                };
            }
        }
        for (const m of topMatches) {
            const start = matchAsUtcDate(m.Dte, m.Hre);
            if (start && start.getTime() > now) {
                let delta = start.getTime() - now;
                const days = Math.floor(delta / 86_400_000);
                delta -= days * 86_400_000;
                const hours = Math.floor(delta / 3_600_000);
                delta -= hours * 3_600_000;
                const minutes = Math.floor(delta / 60_000);
                delta -= minutes * 60_000;
                const seconds = Math.floor(delta / 1000);
                return {kind: 'upcoming' as const, days, hours, minutes, seconds, match: m, start};
            }
        }
        return null;
    }, [topMatches, now]);

    // "compétition courante OU qui arrive" → first enCours, else closest upcoming by DteDeb.
    const featuredCmp = useMemo<CmpEdtRow | null>(() => {
        if (enCours.length > 0) return enCours[0];
        if (aVenir.length === 0) return null;
        const byDebAsc = [...aVenir].sort((a, b) => {
            const da = dateTs(a.DteDeb) ?? Number.POSITIVE_INFINITY;
            const db = dateTs(b.DteDeb) ?? Number.POSITIVE_INFINITY;
            return da - db;
        });
        return byDebAsc[0];
    }, [enCours, aVenir]);

    useEffect(() => {
        if (!featuredCmp) {
            setBannerMatches([]);
            return;
        }
        AxiosServices.getMatches((matches: IMatch[]) => {
            // Store the raw list; the filtering against `now` lives in the
            // useMemos below so a match's state transitions (upcoming →
            // in-progress → past) propagate without a refetch.
            setBannerMatches(matches ?? []);
        }, featuredCmp.Idt).catch((err: any) => console.error('getMatches (banner) failed:', err));
    }, [featuredCmp]);

    // Banner picker: in-progress OR strictly upcoming, oldest start first.
    const openCreate = (cmp: CmpEdtRow) => {
        if (!isLoggedIn) {
            message.warning("Connecte-toi pour créer une inscription.");
            return;
        }
        setPastModalOpen(false);
        setSelected(cmp);
        setCreatedToken(null);
        form.resetFields();
        form.setFieldsValue({lib: ''});
    };

    const closeModal = () => {
        setSelected(null);
        setCreatedToken(null);
        form.resetFields();
    };

    const onSubmit = async (values: {lib: string}) => {
        if (!selected) return;
        const lib = (values.lib ?? '').trim();
        if (!lib) {
            message.warning("Donne un libellé à l'inscription.");
            return;
        }
        setCreating(true);
        try {
            const reg = await AxiosServices.createReg(selected.Idt, lib);
            setCreatedToken(reg.Token);
            message.success(`Inscription « ${reg.Lib} » créée pour ${selected.Lib}.`);
            refreshMyRegs();
        } catch (err: any) {
            const status = err?.response?.status;
            const serverMsg = err?.response?.data?.error ?? err?.response?.data ?? err?.message;
            message.error(`Échec ${status ?? ''} : ${serverMsg ?? 'erreur inconnue'}`);
        } finally {
            setCreating(false);
        }
    };

    const openJoinModal = () => {
        if (!isLoggedIn) {
            message.warning("Connecte-toi pour rejoindre une compétition.");
            return;
        }
        joinForm.resetFields();
        setJoinModalOpen(true);
    };

    const closeJoinModal = () => {
        setJoinModalOpen(false);
        joinForm.resetFields();
    };

    const onJoinGlobal = async (cmp: CmpEdtRow) => {
        if (!isLoggedIn) {
            message.warning("Connecte-toi pour rejoindre une compétition.");
            return;
        }
        try {
            await AxiosServices.joinGlobalReg(cmp.Idt);
            message.success(`Inscription à la compétition globale « ${cmp.Lib} ».`);
            refreshMyRegs();
        } catch (err: any) {
            const status = err?.response?.status;
            const serverMsg = err?.response?.data?.error ?? err?.response?.data ?? err?.message;
            message.error(`Échec ${status ?? ''} : ${serverMsg ?? 'erreur inconnue'}`);
        }
    };

    const onJoinSubmit = async (values: {token: string}) => {
        const token = (values.token ?? '').trim();
        if (!token) {
            message.warning("Colle le token reçu.");
            return;
        }
        setJoining(true);
        try {
            await AxiosServices.joinReg(token);
            message.success("Inscription rejointe.");
            refreshMyRegs();
            closeJoinModal();
        } catch (err: any) {
            const status = err?.response?.status;
            const serverMsg = err?.response?.data?.error ?? err?.response?.data ?? err?.message;
            message.error(`Échec ${status ?? ''} : ${serverMsg ?? 'erreur inconnue'}`);
        } finally {
            setJoining(false);
        }
    };

    const copyToken = async (token: string) => {
        try {
            await navigator.clipboard.writeText(token);
            message.success("Token copié.");
        } catch {
            message.error("Copie impossible.");
        }
    };

    const renderCard = (cmp: CmpEdtRow, status: CmpStatus) => {
        const visuals: Record<CmpStatus, {border: string; opacity: number}> = {
            enCours: {border: ACCENT, opacity: 1},
            aVenir: {border: '#1677ff', opacity: 1},
            passee: {border: 'rgba(0,0,0,0.08)', opacity: 0.78},
        };
        const v = visuals[status];
        const canCreate = status !== 'passee';
        const mineForCmp = regsByCmpEdt.get(cmp.Idt) ?? [];
        return (
            <Col xs={24} sm={12} md={12} lg={12} key={cmp.Idt}>
                <Card
                    hoverable={canCreate}
                    onClick={canCreate ? () => openCreate(cmp) : undefined}
                    style={{
                        borderRadius: 12,
                        borderColor: v.border,
                        opacity: v.opacity,
                        height: '100%',
                        cursor: canCreate ? 'pointer' : 'default',
                    }}
                    bodyStyle={{padding: 16}}
                >
                    <Space direction="vertical" size={6} style={{width: '100%'}}>
                        <Text strong style={{fontSize: 16}}>{cmp.Lib}</Text>
                        {canCreate && (
                            <>
                                <Button type="link" icon={<PlusOutlined/>} style={{padding: 0}}>
                                    Créer votre propre compétition
                                </Button>
                                <Button
                                    type="link"
                                    icon={<LoginOutlined/>}
                                    style={{padding: 0}}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        openJoinModal();
                                    }}
                                >
                                    Rejoindre via un token
                                </Button>
                                <Button
                                    type="link"
                                    icon={<GlobalOutlined/>}
                                    style={{padding: 0}}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onJoinGlobal(cmp);
                                    }}
                                >
                                    Rejoindre la compétition globale
                                </Button>
                            </>
                        )}
                        {mineForCmp.length > 0 && (
                            <div onClick={e => e.stopPropagation()} style={{marginTop: 4, width: '100%'}}>
                                <Text
                                    type="secondary"
                                    style={{
                                        fontSize: 10,
                                        letterSpacing: 0.5,
                                        textTransform: 'uppercase',
                                    }}
                                >
                                    Mes inscriptions
                                </Text>
                                {mineForCmp.map(r => {
                                    const isGlobal = r.Lib === '__global__';
                                    const displayLib = isGlobal ? cmp.Lib : r.Lib;
                                    return (
                                        <div
                                            key={r.idt}
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 4,
                                                marginTop: 4,
                                            }}
                                        >
                                            <Text style={{fontSize: 13, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}>
                                                • {displayLib}
                                            </Text>
                                            {isGlobal ? (
                                                <Tooltip title="Pas de token pour la compétition globale">
                                                    <Text
                                                        type="secondary"
                                                        style={{
                                                            fontFamily: 'monospace',
                                                            fontSize: 11,
                                                            padding: '0 8px',
                                                            letterSpacing: 1,
                                                        }}
                                                    >
                                                        ##########
                                                    </Text>
                                                </Tooltip>
                                            ) : (
                                                <Tooltip title={`Token : ${r.Token} — clic pour copier`}>
                                                    <Button
                                                        size="small"
                                                        type="text"
                                                        icon={<CopyOutlined/>}
                                                        onClick={() => copyToken(r.Token)}
                                                    />
                                                </Tooltip>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </Space>
                </Card>
            </Col>
        );
    };

    return (
        <div style={{padding: 24, maxWidth: 1200, margin: '0 auto'}}>
            <Title level={2} style={{color: ACCENT, textAlign: 'center', marginBottom: 24}}>
                <TrophyOutlined/> Compétitions
            </Title>

            {countdown && (
                <Card
                    style={{
                        borderRadius: 14,
                        border: 'none',
                        background: countdown.kind === 'inProgress'
                            ? 'linear-gradient(135deg, #a02525 0%, #d34a4a 60%, #ef6f4f 100%)'
                            : 'linear-gradient(135deg, #203923 0%, #2d5235 60%, #3a6a45 100%)',
                        boxShadow: countdown.kind === 'inProgress'
                            ? '0 6px 20px rgba(160,37,37,0.30)'
                            : '0 6px 20px rgba(32,57,35,0.25)',
                        marginBottom: 16,
                    }}
                    bodyStyle={{padding: 24}}
                >
                    <Text
                        style={{
                            display: 'block',
                            textAlign: 'center',
                            color: 'rgba(255,255,255,0.78)',
                            fontSize: 12,
                            letterSpacing: 2,
                            textTransform: 'uppercase',
                            marginBottom: 14,
                        }}
                    >
                        {countdown.kind === 'inProgress' ? '● Match en cours' : '⏱ Prochain match dans'}
                    </Text>
                    <div
                        style={{
                            display: 'flex',
                            justifyContent: 'center',
                            gap: 12,
                            flexWrap: 'wrap',
                            marginBottom: 14,
                        }}
                    >
                        {countdown.kind === 'inProgress' ? (
                            <>
                                <CountdownBlock label="Min" value={countdown.elapsedMinutes}/>
                                <CountdownBlock label="Sec" value={countdown.elapsedSeconds}/>
                            </>
                        ) : (
                            <>
                                {countdown.days > 0 && <CountdownBlock label="Jours" value={countdown.days}/>}
                                <CountdownBlock label="Heures" value={countdown.hours}/>
                                <CountdownBlock label="Min" value={countdown.minutes}/>
                                <CountdownBlock label="Sec" value={countdown.seconds}/>
                            </>
                        )}
                    </div>
                    <div style={{textAlign: 'center', color: 'rgba(255,255,255,0.92)', fontSize: 15}}>
                        <strong>{countdown.match.Nom_Dom}</strong>
                        {countdown.kind === 'inProgress' && (countdown.match.Score_Dom ?? -1) !== -1 && (countdown.match.Score_Ext ?? -1) !== -1 ? (
                            <span style={{margin: '0 10px', fontVariantNumeric: 'tabular-nums'}}>
                                <strong>{countdown.match.Score_Dom} — {countdown.match.Score_Ext}</strong>
                            </span>
                        ) : (
                            <span style={{margin: '0 10px', opacity: 0.6}}>vs</span>
                        )}
                        <strong>{countdown.match.Nom_Ext}</strong>
                    </div>
                    <div
                        style={{
                            textAlign: 'center',
                            color: 'rgba(255,255,255,0.55)',
                            fontSize: 11,
                            marginTop: 4,
                        }}
                    >
                        {displayMatchDate(countdown.match.Dte, countdown.match.Hre)} — {displayMatchTime(countdown.match.Dte, countdown.match.Hre)}
                    </div>
                </Card>
            )}

            {featuredCmp && (
                <Card
                    style={{borderRadius: 12, borderColor: ACCENT, marginBottom: 24, background: 'rgba(32,57,35,0.04)'}}
                    bodyStyle={{padding: 16}}
                >
                    <Title level={5} style={{margin: 0, color: ACCENT}}>
                        <CalendarOutlined/> Prochains matchs — {featuredCmp.Lib}
                    </Title>
                    {carouselMatches.length === 0 ? (
                        <Text type="secondary" italic style={{display: 'block', marginTop: 8}}>
                            Aucun match à venir pour cette compétition.
                        </Text>
                    ) : (
                        <Carousel
                            autoplay
                            autoplaySpeed={4500}
                            arrows
                            infinite
                            dots
                            style={{marginTop: 12, padding: '0 32px'}}
                        >
                            {carouselMatches.map(m => (
                                <div key={m.Idt}>
                                    <Card
                                        size="small"
                                        bodyStyle={{padding: 16}}
                                        style={{
                                            margin: '0 8px 8px',
                                            borderRadius: 10,
                                            textAlign: 'center',
                                        }}
                                    >
                                        <Text type="secondary" style={{fontSize: 12}}>
                                            <CalendarOutlined/> {displayMatchDate(m.Dte, m.Hre)} — {displayMatchTime(m.Dte, m.Hre)}
                                        </Text>
                                        <div
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                gap: 12,
                                                marginTop: 8,
                                            }}
                                        >
                                            <img
                                                src={`/images/${m.team_idt_Dom}.png`}
                                                alt={m.Nom_Dom}
                                                style={{width: 36, height: 36, objectFit: 'contain', flexShrink: 0}}
                                                onError={(e) => { (e.currentTarget as HTMLImageElement).style.visibility = 'hidden'; }}
                                            />
                                            <Text strong style={{fontSize: 16}}>{m.Nom_Dom}</Text>
                                            <Text type="secondary" style={{margin: '0 4px'}}>vs</Text>
                                            <Text strong style={{fontSize: 16}}>{m.Nom_Ext}</Text>
                                            <img
                                                src={`/images/${m.team_idt_Ext}.png`}
                                                alt={m.Nom_Ext}
                                                style={{width: 36, height: 36, objectFit: 'contain', flexShrink: 0}}
                                                onError={(e) => { (e.currentTarget as HTMLImageElement).style.visibility = 'hidden'; }}
                                            />
                                        </div>
                                    </Card>
                                </div>
                            ))}
                        </Carousel>
                    )}
                </Card>
            )}

            {myCompetitions.length > 0 && (
                <>
                    <Title level={4} style={{color: ACCENT, marginTop: 8}}>
                        <StarFilled style={{color: '#faad14'}}/> Mes compétitions
                    </Title>
                    <Row gutter={[16, 16]} style={{marginBottom: 32}}>
                        {myCompetitions.map(c => renderCard(c, classify(c)))}
                    </Row>
                    <Title level={4} style={{color: 'rgba(0,0,0,0.65)', marginTop: 8}}>
                        <TrophyOutlined/> Toutes les compétitions
                    </Title>
                </>
            )}

            <Row gutter={[24, 24]}>
                <Col xs={24} md={12}>
                    <Title level={4} style={{color: ACCENT, marginTop: 8}}>
                        <TrophyOutlined/> En cours
                    </Title>
                    {enCours.length === 0 ? (
                        <Empty description="Aucune compétition en cours."/>
                    ) : (
                        <Row gutter={[16, 16]}>{enCours.map(c => renderCard(c, 'enCours'))}</Row>
                    )}
                </Col>
                <Col xs={24} md={12}>
                    <Title level={4} style={{color: '#1677ff', marginTop: 8}}>
                        <CalendarOutlined/> À venir
                    </Title>
                    {aVenir.length === 0 ? (
                        <Empty description="Aucune compétition à venir."/>
                    ) : (
                        <Row gutter={[16, 16]}>{aVenir.map(c => renderCard(c, 'aVenir'))}</Row>
                    )}
                </Col>
            </Row>

            <Card
                hoverable={passees.length > 0}
                onClick={() => { if (passees.length > 0) setPastModalOpen(true); }}
                style={{
                    borderRadius: 12,
                    marginTop: 32,
                    background: 'rgba(0,0,0,0.02)',
                    borderColor: 'rgba(0,0,0,0.08)',
                    cursor: passees.length > 0 ? 'pointer' : 'default',
                }}
                bodyStyle={{padding: 16, textAlign: 'center'}}
            >
                <Space>
                    <HistoryOutlined style={{color: 'rgba(0,0,0,0.45)'}}/>
                    <Text strong style={{color: 'rgba(0,0,0,0.65)'}}>
                        Voir les compétitions passées ({passees.length})
                    </Text>
                    {passees.length > 0 && <RightOutlined style={{color: 'rgba(0,0,0,0.35)'}}/>}
                </Space>
            </Card>

            <Modal
                open={pastModalOpen}
                onCancel={() => setPastModalOpen(false)}
                title={<><HistoryOutlined/> Compétitions passées</>}
                footer={null}
                width={900}
                destroyOnClose
            >
                {passees.length === 0 ? (
                    <Text type="secondary" italic>Aucune compétition passée.</Text>
                ) : (
                    <Row gutter={[16, 16]}>{passees.map(c => renderCard(c, 'passee'))}</Row>
                )}
            </Modal>

            <Modal
                open={joinModalOpen}
                onCancel={closeJoinModal}
                title={<><LoginOutlined/> Rejoindre une inscription</>}
                onOk={() => joinForm.submit()}
                okText="Rejoindre"
                cancelText="Annuler"
                confirmLoading={joining}
                destroyOnClose
            >
                <Form
                    form={joinForm}
                    layout="vertical"
                    onFinish={onJoinSubmit}
                    requiredMark={false}
                    initialValues={{token: ''}}
                >
                    <Form.Item
                        name="token"
                        label="Token d'invitation"
                        rules={[
                            {required: true, message: 'Token requis.'},
                            {len: 10, message: 'Le token fait 10 caractères.'},
                        ]}
                        getValueFromEvent={(e) => (e?.target?.value ?? '').toLowerCase()}
                    >
                        <Input
                            autoFocus
                            placeholder="Colle le token reçu"
                            style={{fontFamily: 'monospace'}}
                            maxLength={10}
                        />
                    </Form.Item>
                </Form>
            </Modal>

            <Modal
                open={!!selected}
                onCancel={closeModal}
                title={selected ? `Nouvelle inscription — ${selected.Lib}` : 'Nouvelle inscription'}
                footer={createdToken ? [
                    <Button key="ok" type="primary" onClick={closeModal}>Fermer</Button>,
                ] : undefined}
                onOk={() => form.submit()}
                okText="Créer"
                cancelText="Annuler"
                confirmLoading={creating}
                destroyOnClose
            >
                {createdToken ? (
                    <Alert
                        type="success"
                        showIcon
                        message="Inscription créée"
                        description={
                            <Space direction="vertical" style={{width: '100%'}} size={6}>
                                <Text type="secondary">
                                    Tu es Admin de cette inscription. Partage le token ci-dessous avec les
                                    membres qui doivent la rejoindre :
                                </Text>
                                <Space.Compact style={{width: '100%'}}>
                                    <Input
                                        readOnly
                                        value={createdToken}
                                        style={{fontFamily: 'monospace'}}
                                    />
                                    <Tooltip title="Copier">
                                        <Button
                                            icon={<CopyOutlined/>}
                                            onClick={() => copyToken(createdToken)}
                                        />
                                    </Tooltip>
                                </Space.Compact>
                            </Space>
                        }
                    />
                ) : (
                    <Form
                        form={form}
                        layout="vertical"
                        onFinish={onSubmit}
                        requiredMark={false}
                        initialValues={{lib: ''}}
                    >
                        <Form.Item
                            name="lib"
                            label="Libellé de l'inscription"
                            rules={[
                                {required: true, message: 'Libellé requis.'},
                                {max: 100, message: '100 caractères max.'},
                            ]}
                        >
                            <Input
                                autoFocus
                                placeholder="Ex : Bureau du second étage"
                                maxLength={100}
                            />
                        </Form.Item>
                    </Form>
                )}
            </Modal>
        </div>
    );
};

export default Home;
